import React from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TreePine, 
  Gavel, 
  TrendingUp, 
  Plus, 
  FileText,
  Calendar,
  MapPin,
  Eye,
  ExternalLink
} from 'lucide-react';

const ProjectMarketplace = () => {
  const mockListings = [
    {
      id: 1,
      projectName: "Solar Farm Maharashtra",
      developer: "GreenTech Solutions",
      credits: 50000,
      pricePerCredit: 850,
      location: "Maharashtra, India",
      type: "Solar Energy",
      vintage: 2024,
      status: "Active"
    },
    {
      id: 2,
      projectName: "Wind Power Tamil Nadu",
      developer: "Renewable Energy Corp",
      credits: 75000,
      pricePerCredit: 920,
      location: "Tamil Nadu, India",
      type: "Wind Energy",
      vintage: 2024,
      status: "Active"
    },
    {
      id: 3,
      projectName: "Biogas Karnataka",
      developer: "Clean Energy Ltd",
      credits: 30000,
      pricePerCredit: 780,
      location: "Karnataka, India",
      type: "Biogas",
      vintage: 2023,
      status: "Active"
    }
  ];

  const mockAuctions = [
    {
      id: 1,
      title: "Q1 2025 Carbon Credit Auction",
      credits: 100000,
      currentBid: 875,
      endDate: "2025-01-15",
      participants: 24
    },
    {
      id: 2,
      title: "Renewable Energy Credits - Batch 5",
      credits: 250000,
      currentBid: 945,
      endDate: "2025-01-20",
      participants: 31
    }
  ];

  const mockProposals = [
    {
      id: 1,
      rfpTitle: "Corporate Carbon Neutrality Project",
      submittedDate: "2024-12-28",
      budget: "₹2.5 Cr",
      status: "Under Review"
    },
    {
      id: 2,
      rfpTitle: "Industrial Decarbonization Initiative",
      submittedDate: "2024-12-25",
      budget: "₹1.8 Cr",
      status: "Shortlisted"
    }
  ];

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Project Developer - Marketplace</h1>
          <p className="text-muted-foreground">
            Browse marketplace opportunities and manage your auction proposals
          </p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Project Developer View:</strong> Browse projects and rates for market research. 
              Create live auctions for your projects and manage RFP proposals.
            </p>
          </div>
        </div>

        <Tabs defaultValue="browse" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="browse">Browse Projects</TabsTrigger>
            <TabsTrigger value="auctions">Live Auctions</TabsTrigger>
            <TabsTrigger value="create">Create Auction</TabsTrigger>
            <TabsTrigger value="proposals">My Proposals & RFPs</TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TreePine className="w-5 h-5" />
                  Available Projects - Market Research
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {mockListings.map((listing) => (
                    <Card key={listing.id} className="border hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{listing.projectName}</h3>
                              <Badge variant="outline">{listing.type}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                              <span>Developer: {listing.developer}</span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {listing.location}
                              </span>
                              <span>Vintage: {listing.vintage}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-lg font-bold text-primary">
                                ₹{listing.pricePerCredit}/credit
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {listing.credits.toLocaleString()} credits available
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </Button>
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Registry
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="auctions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gavel className="w-5 h-5" />
                  Live Auctions - Participate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {mockAuctions.map((auction) => (
                    <Card key={auction.id} className="border-2 border-blue-200">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2">{auction.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                              <span>{auction.credits.toLocaleString()} credits</span>
                              <span>{auction.participants} participants</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Ends: {auction.endDate}
                              </span>
                            </div>
                            <div className="text-lg font-bold text-green-600">
                              Current Bid: ₹{auction.currentBid}/credit
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button>
                              <Gavel className="w-4 h-4 mr-2" />
                              Place Bid
                            </Button>
                            <Button variant="outline" size="sm">
                              View Details
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="create" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Create Live Auction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-2xl mx-auto space-y-6">
                  <div className="text-center py-8">
                    <Gavel className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Set Up Your Carbon Credit Auction</h3>
                    <p className="text-muted-foreground mb-6">
                      Create competitive auctions for your carbon credits and maximize revenue
                    </p>
                    <Button size="lg">
                      <Plus className="w-5 h-5 mr-2" />
                      Start Auction Setup
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-4 border-2 border-dashed hover:border-primary cursor-pointer">
                      <div className="text-center">
                        <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <h4 className="font-semibold">Standard Auction</h4>
                        <p className="text-sm text-muted-foreground">Traditional bidding format</p>
                      </div>
                    </Card>
                    <Card className="p-4 border-2 border-dashed hover:border-primary cursor-pointer">
                      <div className="text-center">
                        <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <h4 className="font-semibold">Reserve Auction</h4>
                        <p className="text-sm text-muted-foreground">Set minimum price threshold</p>
                      </div>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="proposals" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  My Proposals & RFPs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockProposals.map((proposal) => (
                    <Card key={proposal.id} className="border">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2">{proposal.rfpTitle}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                              <span>Submitted: {proposal.submittedDate}</span>
                              <span>Budget: {proposal.budget}</span>
                            </div>
                            <Badge 
                              variant={proposal.status === 'Shortlisted' ? 'default' : 'secondary'}
                            >
                              {proposal.status}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </Button>
                            <Button variant="outline" size="sm">
                              <FileText className="w-4 h-4 mr-2" />
                              Update
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ProjectMarketplace;